/**
 * Hook for generating and managing property reports (PDF)
 */

import { useState, useCallback, useRef, useEffect } from 'react';

interface GenerateReportOptions {
  /** Whether to include AI-generated content (default: true) */
  includeAIContent?: boolean;
  /** Whether to include repair images in the PDF (default: true) */
  includeImages?: boolean;
  /** Custom title for the report */
  customTitle?: string;
}

interface UsePropertyReportReturn {
  /** Generate and download a property report PDF */
  generateReport: (homeId: string, options?: GenerateReportOptions) => Promise<void>;
  /** Whether report is currently being generated */
  isGenerating: boolean;
  /** Error message if generation failed */
  error: string | null;
  /** Clear any error state */
  clearError: () => void;
  /** Cancel any in-flight report generation request */
  cancelGeneration: () => void;
}

/**
 * Custom hook for generating property condition reports
 */
export function usePropertyReport(): UsePropertyReportReturn {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const cancelGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsGenerating(false);
    }
  }, []);

  const generateReport = useCallback(async (
    homeId: string,
    options: GenerateReportOptions = {}
  ): Promise<void> => {
    if (!homeId) {
      setError('Home ID is required');
      return;
    }

    // Abort any previous in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsGenerating(true);
    setError(null);

    try {
      const response = await fetch(`/api/homes/${homeId}/report/pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        signal: abortController.signal,
        body: JSON.stringify({
          includeAIContent: options.includeAIContent ?? true,
          includeImages: options.includeImages ?? true,
          customTitle: options.customTitle,
        }),
      });

      if (!response.ok) {
        // Try to extract error message from JSON response
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to generate report: ${response.statusText}`);
        }
        throw new Error(`Failed to generate report: ${response.statusText}`);
      }

      // Get the PDF blob
      const blob = await response.blob();

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'property-report.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch?.[1]) {
          filename = filenameMatch[1];
        }
      }

      // Create download link and trigger download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL
      URL.revokeObjectURL(url);
    } catch (err) {
      // Don't set error for abort (user cancelled)
      if (err instanceof Error && err.name === 'AbortError') {
        console.log('[usePropertyReport] Report generation cancelled');
        return;
      }

      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('[usePropertyReport] Error generating report:', err);
    } finally {
      // Clear the abort controller ref if it matches current request
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
      setIsGenerating(false);
    }
  }, []);

  return {
    generateReport,
    isGenerating,
    error,
    clearError,
    cancelGeneration,
  };
}

export default usePropertyReport;
